import { Language } from '../types/game'

/**
 * Service for handling language-specific functionality in the game
 */
export class LanguageService {
  private static instance: LanguageService

  // Private constructor for singleton pattern
  private constructor() {}

  /**
   * Gets the singleton instance of the service
   */
  public static getInstance(): LanguageService {
    if (!LanguageService.instance) {
      LanguageService.instance = new LanguageService()
    }
    return LanguageService.instance
  }

  /**
   * Gets a filtered keyboard layout based on the language configuration
   * Removes any characters specified in language.excludedCharacters
   * Adds any special characters specified in language.specialCharacters
   */
  public getKeyboardLayout(language: Language): string[][] {
    const defaultLayout = [
      ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
      ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
      ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DELETE'],
    ]

    // Filter out excluded characters
    const filteredLayout = defaultLayout.map((row) =>
      row.filter(
        (key) =>
          !language.excludedCharacters.includes(key) ||
          key === 'ENTER' ||
          key === 'DELETE'
      )
    )

    // Add special characters to the second row if they exist
    if (language.specialCharacters && language.specialCharacters.length > 0) {
      // Add special characters to the middle row
      filteredLayout[1] = [...filteredLayout[1], ...language.specialCharacters]
    }

    return filteredLayout
  }

  /**
   * Checks if a character is valid for the given language
   * Returns false if the character is in the excludedCharacters list
   * Returns true if the character is in the specialCharacters list
   */
  public isValidCharacter(char: string, language: Language): boolean {
    // Convert to uppercase for standard letters
    const uppercaseChar = char.toUpperCase()

    // Check for special characters first (case sensitive)
    if (language.specialCharacters?.includes(char)) {
      return true
    }

    // Then check if it's not in the excluded characters list
    return !language.excludedCharacters.includes(uppercaseChar)
  }

  /**
   * Gets language information by ISO code
   */
  public getLanguageByCode(code: string): Partial<Language> {
    const languageMappings: Record<string, Partial<Language>> = {
      en: {
        name: 'English',
        excludedCharacters: [],
      },
      sn: {
        name: 'Shona',
        excludedCharacters: ['X'],
        specialCharacters: ["'"],
      },
    }

    return languageMappings[code] || languageMappings['en'] // Default to English
  }

  /**
   * Gets a localized error message for invalid character
   */
  public getInvalidCharacterMessage(char: string, language: Language): string {
    return `'${char}' is not used in ${language.name}`
  }
}
